import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormikProps, useFormik } from 'formik';
import { AccommodationPlaceCategory, Maybe, useJoyPlaceIdBySourceIdLazyQuery } from '@graphql/generated';
import { ChevronDown, Close, Help, Trash, WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { Flex, FormControl, SpacingStack, useToast, Divider, ButtonV2, TextArea, InputV2, SelectV1, useDisclosure, Box, TextV2, CheckboxV2, TooltipV2 } from '@withjoy/joykit';
import { StyledInputContainer } from '@withjoy/joykit/components/InputV1/StyledInputContainer';
import { useFieldErrors } from '@shared/createEvent/hooks/useFieldErrors';
import { CustomPropertyFormFields, CustomPropertyFormProps } from './CustomPropertyForm.types';
import { LocationInput } from '@shared/components/LocationInput';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { useAuth } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHotelPhoto } from '../../hooks/useHotelPhoto';
import { getHotelAddress } from '../../utils/Accommodations.utils';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useHandleOnSelectHotel } from '../../hooks/useHandleOnSelectHotel';

import { useEventInfo } from '@shared/utils/eventInfo';
import StyledToggle from '../../components/StyledToggle';
import { HotelRoomBlockForm, useHotelRoomBlockFormSchema } from '../../components/HotelRoomBlockForm';
import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import { calculateStayDates, createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { MatchedHotelDialog } from '../../components/MatchedHotelDialog';
import { useHotelRedirectUrl } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelRedirectUrl';
import { SaveCancelButtons } from '@website-editor/features/custom-page/components/SaveCancelButtons/SaveCancelButtons';
import { PagePhoto } from '@website-editor/components/PagePhoto';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCustomComponentStateContext } from '@apps/admin/routes/WebsiteEditor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';

const MAX_LENGTH = 1500;

export const CustomPropertyForm: React.FC<CustomPropertyFormProps> = props => {
  const { customPropertyData: customPropertyData, isLoading, isEditMode, containerId, handleCancel, handleSubmit, handleDelete, handleCancelRoomBlock } = props;
  const { initialRoomBlockEnabled } = useCustomComponentStateContext();

  const theme = useTheme();
  const { toast } = useToast();
  const { tq } = useTranslation('travelMap');

  const [accommodationsCustomPropertyPhotoEnabled] = useIsFeatureEnabled('accommodationsCustomPropertyPhoto');

  const { isOpen: isMatchedHotelDialogOpen, onOpen: onOpenMatchedHotelDialog, onClose: onCloseMatchedHotelDialog } = useDisclosure();

  const { isSuperAdmin } = useAuth();
  const { eventInfo } = useEventInfo();
  const { checkIn, checkOut } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const [joyPlaceIdBySourceId, { data: joyPlaceIdBySourceIdData }] = useJoyPlaceIdBySourceIdLazyQuery();

  const [loadingLocation, setLoadingLocation] = useState(false);
  const [hotelInventoryData, setHotelInventoryData] = useState<HotelInventory | null>(null);

  const onShowMessage = useEventCallback((show: boolean, sourceId?: string, hotelInventory?: HotelInventory) => {
    if (show && sourceId) {
      if (!hotelInventory?.joyPlaceId) {
        joyPlaceIdBySourceId({ variables: { payload: { eventId: eventInfo?.eventId, sourceId, sourceType: 'zh' } } });
      }
      setHotelInventoryData(hotelInventory || null);
      onOpenMatchedHotelDialog();
    }
  });

  const address =
    customPropertyData.customAddress ||
    getHotelAddress({
      address: customPropertyData.address,
      city: customPropertyData.city,
      state: customPropertyData.state,
      postalCode: customPropertyData.postalCode,
      country: customPropertyData.country,
    });

  const telemetry = useAdminAccommodationsTelemetry();
  useEffect(() => {
    if (isEditMode)
      telemetry.editItemClicked('accommodations.AccommodationPlaces', {
        itemType: TravelMapBlockType.travelMapAccommodationsCustom,
        isSuperAdmin,
        zhId: customPropertyData.zhId,
        displayName: customPropertyData.name,
        customName: customPropertyData.customName,
        url: customPropertyData.url,
        customUrl: customPropertyData.customUrl,
        isMessageShowing: isMatchedHotelDialogOpen,
        accommodationAction: 'accommodationEdit',
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMatchedHotelDialogOpen]);

  const hotelRoomBlockSchema = useHotelRoomBlockFormSchema();
  const formik = useFormik<CustomPropertyFormFields>({
    initialValues: {
      name: customPropertyData.name || customPropertyData.customName,
      address: address,
      phone: customPropertyData.customPhone || customPropertyData.phone,

      customName: customPropertyData.customName || customPropertyData.name,
      customAddress: address,
      customPhone: customPropertyData.customPhone || customPropertyData.phone,

      url: customPropertyData.url,
      joyPlaceId: customPropertyData.joyPlaceId,
      customUrl: customPropertyData.customUrl,
      isCustomUrl: !!customPropertyData.customUrl,
      postalCode: customPropertyData.postalCode,
      country: customPropertyData.country,
      latitude: parseFloat(customPropertyData.lat?.toString() ?? ''),
      longitude: parseFloat(customPropertyData.long?.toString() ?? ''),
      note: customPropertyData.userNote,
      customPhotoId: customPropertyData.customPhoto?.id ?? undefined,
      zhId: customPropertyData.zhId,
      googlePlaceId: customPropertyData.googlePlaceId,
      type: customPropertyData.type,
      category: customPropertyData.category || AccommodationPlaceCategory.hotel,

      isFavorite: customPropertyData.isFavorite ?? false,
      isCustomLinkEnabled: customPropertyData.isCustomLinkEnabled ?? false,
      isStayingHere: customPropertyData.isHostStayingHere ?? false,
      isHotelRoomBlock: initialRoomBlockEnabled || !!customPropertyData.accommodationRoomBlock?.bookingUrl,
      photoUrl: customPropertyData.isPhotoHidden ? undefined : (customPropertyData.photo?.url ?? undefined),
      bookingUrl: customPropertyData.accommodationRoomBlock?.bookingUrl,
      isBookingLinkDisabled: !customPropertyData.accommodationRoomBlock?.bookingUrl,
      isJoyManagedRoomBlock: Boolean(customPropertyData.accommodationRoomBlock?.isJoyManagedRoomBlock),
      checkInDate: customPropertyData.accommodationRoomBlock?.checkInDate
        ? createDateFromUnformattedString(customPropertyData.accommodationRoomBlock?.checkInDate)
        : eventInfo?.finalizedDate?.dateString
          ? checkIn.date
          : undefined,
      checkOutDate: customPropertyData.accommodationRoomBlock?.checkOutDate
        ? createDateFromUnformattedString(customPropertyData.accommodationRoomBlock?.checkOutDate)
        : eventInfo?.finalizedDate?.dateString
          ? checkOut.date
          : undefined,
      cutoffDate: customPropertyData.accommodationRoomBlock?.cutoffDate ? createDateFromUnformattedString(customPropertyData.accommodationRoomBlock?.cutoffDate) : undefined,
      numberOfRoomsReserved: customPropertyData.accommodationRoomBlock?.numberOfRoomsReserved ?? 0,
      strikeoutPricePerNight: customPropertyData.accommodationRoomBlock?.strikeoutPricePerNight ?? 0,
      pricePerNight: customPropertyData.accommodationRoomBlock?.pricePerNight ?? 0,
    },
    validationSchema: Yup.object({
      customName: Yup.string().required(tq`Please search for a hotel`),
      address: Yup.string()
        .required(tq`Please add an address`)
        .test('location-selected', tq`Please select an address from the dropdown`, function (value) {
          const { latitude, longitude } = this.parent;

          if (!value) return true;

          const lat = Number(latitude);
          const lng = Number(longitude);
          const hasValidCoords = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

          return hasValidCoords;
        }),
      customUrl: Yup.string()
        .nullable()
        .trim()
        .url(tq`Please enter a valid URL.`),
      isFavorite: Yup.boolean().nullable(),
      isCustomLinkEnabled: Yup.boolean().nullable(),
      isStayingHere: Yup.boolean().nullable(),
      isHotelRoomBlock: Yup.boolean().nullable(),
      ...hotelRoomBlockSchema,
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      if (!(values.zhId || values.joyPlaceId || values.googlePlaceId)) {
        toast(tq`An error occurred while selecting the hotel. Please try again.`, { icon: <WarningTriangle /> });
        formik.setFieldError('address', tq`An error occurred while selecting the hotel. Please try again.`);
        return;
      }

      handleSubmit({ ...values, customUrl: values.customUrl });
      telemetry.saveButtonClicked('accommodations', {
        itemType: TravelMapBlockType.travelMapAccommodationsCustom,
        zhId: values.zhId,
        accommodationAction: isEditMode ? 'edited' : 'added',
        usingCustomLink: true,
        customLink: values.customUrl || '',
      });
    },
  });

  const errors = formik.errors;
  const getFieldErrorProps = useFieldErrors<CustomPropertyFormFields>(formik);

  const onSaveError = useEventCallback((message: string) => {
    toast(message, { icon: <WarningTriangle /> });
  });

  const { hotelPhoto, isUploadingPhoto, hasDefaultPhoto, onUpdateHotelPhoto, onDeleteHotelPhoto, onResetHotelPhoto, setAutoGeneratedPhoto, setHotelPhoto } = useHotelPhoto({
    containerId,
    formik,
    hotelPhoto: customPropertyData.isPhotoHidden ? undefined : customPropertyData.displayPhoto?.url,
    defaultPhoto: customPropertyData.photo?.url,
    onSaveError,
  });

  const updateFormAfterFetchHotelDetails = useEventCallback((updatedValues: CustomPropertyFormFields) => {
    formik.setValues(updatedValues);
    setLoadingLocation(false);
  });

  const { handleOnSelectHotel } = useHandleOnSelectHotel({
    isHotelForm: false,
    setShowMessage: onShowMessage,
    setHotelPhoto,
    setAutoGeneratedPhoto,
    updateFormAfterFetchHotelDetails,
    isUpdateCustomUrlLocally: true,
  });

  const handleOnCloseMatchedHotelDialog = useEventCallback(() => {
    onCloseMatchedHotelDialog();
    // Skip validation to avoid a false "Select location" error when the dialog closes
    // (lat/lng from LocationInput may update asynchronously).
    formik.setFieldValue('isCustomLinkEnabled', true, false);
  });

  const handleSearchLocationV1 = useEventCallback((location: Maybe<Location>) => {
    setLoadingLocation(true);
    handleOnSelectHotel({ ...formik.values }, location);
  });

  const getLengthOfStringField = (key: string): number => {
    const value = formik.values[key];
    if (value && typeof value === 'string') {
      return value.length;
    }
    return 0;
  };

  const typeOptions = [
    { value: AccommodationPlaceCategory.hotel, label: tq`Hotel` },
    { value: AccommodationPlaceCategory.rental, label: tq`House / Rental` },
    { value: AccommodationPlaceCategory.campsite, label: tq`Tent / Campsite` },
  ];

  const isHotelInvalid = !!formik.touched.address && !!errors.address;
  const errorHotel = formik.touched.address && errors.address ? errors.address : undefined;

  const hotelUrl = useHotelRedirectUrl({
    hotel: { ...formik.values, type: TravelMapBlockType.travelMapAccommodationsCustom },
    searchParams: {
      skipAvailabilityCheck: 'true',
      utm_medium: 'web',
      utm_source: 'admin_accommodations',
      utm_campaign: 'custom_property_form',
    },
  });

  const handleSave = useEventCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    formik.submitForm();
  });

  return (
    <>
      <form
        onSubmit={formik.handleSubmit}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', position: 'relative' }}
      >
        <Flex
          flexDirection="column"
          gap={7}
          paddingBottom={20}
        >
          <SpacingStack spacing={7}>
            <FormControl
              label={tq`Location`}
              isRequired={false}
              {...getFieldErrorProps('address')}
            >
              <StyledHotelInputContainer
                isHotelInvalid={isHotelInvalid}
                errorHotel={errorHotel}
              >
                <LocationInput
                  {...formik.getFieldProps('address')}
                  onInputChange={val => {
                    formik.setFieldValue('address', val);
                    formik.setFieldValue('customAddress', val);
                  }}
                  onLocationOpen={() => {
                    formik.setFieldTouched('address');
                  }}
                  onLocationChange={handleSearchLocationV1}
                  placeholder={' '}
                  loading={loadingLocation}
                  includeMapIcon={false}
                  placePredictionTypes={[]}
                  allowCustomPlace={false}
                  defaultValue={formik.values.address as string}
                  formik={formik}
                  css={{ ['input']: { fontSize: pxToRem(16) } }}
                />
              </StyledHotelInputContainer>
            </FormControl>
            <Divider />
            <FormControl
              width="100%"
              marginTop={2}
              label={tq`Name`}
              isRequired={false}
              {...getFieldErrorProps('customName')}
            >
              <InputV2
                {...formik.getFieldProps('customName')}
                {...getFieldErrorProps('customName')}
              />
            </FormControl>
            {/* Photo - Don't show if the hotel has a room block for custom property which already has a photo field */}
            {accommodationsCustomPropertyPhotoEnabled && !formik.values.isHotelRoomBlock && (
              <FormControl {...getFieldErrorProps('photoUrl')}>
                <Box
                  padding={3}
                  borderRadius={2}
                  border="1px solid"
                  borderColor="mono3"
                  __css={{ boxShadow: !!formik.touched.photoUrl && !!formik.errors.photoUrl ? `0px 0 0px 2px ${theme.colors.inputBorderError}` : 'none' }}
                >
                  <Box width="min-content">
                    <PagePhoto
                      handleUpdatePhoto={onUpdateHotelPhoto}
                      handleDeletePhoto={onDeleteHotelPhoto}
                      handleResetPhoto={onResetHotelPhoto}
                      hasDefaultPhoto={hasDefaultPhoto}
                      loading={isUploadingPhoto}
                      photo={hotelPhoto}
                      label={hotelPhoto ? tq`Edit Photo` : tq`Add Photo`}
                    />
                  </Box>
                </Box>
              </FormControl>
            )}
            <Flex
              marginTop={2}
              width="100%"
              flexDirection={'column'}
            >
              <FormControl
                width="100%"
                label={tq`Booking Link`}
                isRequired={false}
                {...getFieldErrorProps('customUrl')}
              >
                <InputV2
                  isDisabled={!formik.values.isCustomLinkEnabled}
                  {...formik.getFieldProps('customUrl')}
                  {...getFieldErrorProps('customUrl')}
                  placeholder={formik.values.isCustomLinkEnabled ? undefined : tq`Use Joy Travel`}
                />
              </FormControl>
              <CheckboxV2
                typographyVariant={'body2'}
                marginTop={5}
                isChecked={Boolean(formik.values.isCustomLinkEnabled)}
                {...formik.getFieldProps('isCustomLinkEnabled')}
                {...getFieldErrorProps('isCustomLinkEnabled')}
              >
                <Flex
                  alignItems="center"
                  gap={1}
                >
                  {tq`Use My Own Link`}
                  <TooltipV2
                    placement="topStart"
                    content={
                      <Box width={pxToRem(230)}>
                        <TextV2
                          typographyVariant="label3"
                          color="typePrimaryLight"
                        >
                          {tq`Hotel Link Tip`}
                        </TextV2>
                        <TextV2
                          typographyVariant="label2"
                          color="mono4"
                        >
                          {tq`Got your own booking link? Use it here! If you uncheck this, we'll link to the hotel on Joy — where your guests can book rooms at a special group rate just for them (not available to the public) and reserve right from your wedding site.`}
                        </TextV2>
                      </Box>
                    }
                  >
                    <Help
                      size={16}
                      color="mono8"
                    />
                  </TooltipV2>
                  {(formik.values.zhId || hotelInventoryData?.joyPlaceId) && !formik.values.isCustomLinkEnabled && hotelUrl && (
                    <ButtonV2
                      as="a"
                      href={hotelUrl}
                      target="_blank"
                      typographyVariant={'label3'}
                      fontSize={14}
                      fontWeight={400}
                      color={'newUITest1'}
                      intent="newPrimary"
                      variant="ghost"
                      textDecoration={'none'}
                      paddingLeft={{ _: 1, sm2: 3 }}
                      paddingRight={0}
                      height={'auto'}
                    >
                      {tq`View hotel on Joy`}
                    </ButtonV2>
                  )}
                </Flex>
              </CheckboxV2>
            </Flex>
            <FormControl
              width="100%"
              marginTop={2}
              label={tq`Type`}
              isRequired={false}
              {...getFieldErrorProps('type')}
            >
              <SelectV1
                {...formik.getFieldProps('category')}
                {...getFieldErrorProps('category')}
                value={typeOptions.find(option => option.value === formik.values.category)}
                onChange={e => {
                  formik.setFieldValue('category', e?.value);
                }}
                iconRight={<ChevronDown />}
                options={typeOptions}
                searchable={false}
              />
            </FormControl>
            <FormControl
              marginTop={2}
              isRequired={false}
              caption={`${getLengthOfStringField('note')}/${MAX_LENGTH}`}
              overrides={{ Hint: { props: { color: 'mono10', textAlign: 'right' } } }}
              label={tq`Note for Guests`}
              {...getFieldErrorProps('note')}
            >
              <StyledTextAreaContainer isInvalid={getFieldErrorProps('note').isInvalid}>
                <TextArea
                  {...formik.getFieldProps('note')}
                  maxLength={MAX_LENGTH}
                  placeholder={tq`A note to share with your guests about this hotel.`}
                  resize="vertical"
                />
              </StyledTextAreaContainer>
            </FormControl>
            <StyledToggle
              label={tq`We're Staying Here`}
              sideNote={tq`Let your guests know where you're staying.`}
              name="isStayingHere"
              overrides={{ Root: { props: { marginTop: 6, marginBottom: isEditMode && formik.values.isHotelRoomBlock ? 6 : 0 } } }}
              tooltip={{
                title: tq`Where You're Staying`,
                content: tq`Let guest know where you'll be staying. We'll put this hotel first on your page and highlight it letting guest know with a label.`,
              }}
              isChecked={!!formik.values.isStayingHere}
              onChange={() => formik.setFieldValue('isStayingHere', !formik.values.isStayingHere)}
            />
            {!isEditMode && (
              <StyledToggle
                label={tq`Hotel Room Block`}
                name="isHotelRoomBlock"
                overrides={{ Root: { props: { marginTop: 6 } } }}
                tooltip={{ title: tq`Room Block`, content: tq`Let guests know this is a group room block. You'll be able to set the booking deadline and discount price.` }}
                isChecked={!!formik.values.isHotelRoomBlock}
                onChange={() => formik.setFieldValue('isHotelRoomBlock', !formik.values.isHotelRoomBlock)}
              />
            )}
            {formik.values.isHotelRoomBlock && (
              <HotelRoomBlockForm
                type={TravelMapBlockType.travelMapAccommodationsCustom}
                formik={formik as FormikProps<CustomPropertyFormFields>}
                containerId={containerId}
                data={customPropertyData}
                isEditMode={isEditMode}
              />
            )}
          </SpacingStack>
        </Flex>
        <Box>
          {isEditMode && handleDelete && (isSuperAdmin || !formik?.values?.isJoyManagedRoomBlock) && (
            <Flex
              justifyContent="center"
              alignItems="center"
              marginTop={8}
            >
              <Flex
                gap={3}
                cursor="pointer"
                alignItems="center"
                onClick={() => handleDelete()}
              >
                <Trash
                  size={24}
                  color="negative6"
                />
                <TextV2
                  typographyVariant="button2"
                  color="negative6"
                >
                  {tq`Remove Stay from Page`}
                </TextV2>
              </Flex>
            </Flex>
          )}
          {isEditMode && formik?.values?.isJoyManagedRoomBlock && (
            <Flex
              justifyContent="center"
              alignItems="center"
              marginTop={6}
            >
              <Flex
                gap={3}
                cursor="pointer"
                alignItems="center"
                onClick={handleCancelRoomBlock}
              >
                <Close
                  size={24}
                  color="negative6"
                />
                <TextV2
                  typographyVariant="button2"
                  color="negative6"
                >
                  {tq`Cancel Room Block`}
                </TextV2>
              </Flex>
            </Flex>
          )}
        </Box>
        <SaveCancelButtons
          onCancel={handleCancel}
          onSave={handleSave}
          isLoading={isLoading}
          isSaveDisabled={loadingLocation || !formik.isValid || !formik.dirty}
        />
      </form>
      <MatchedHotelDialog
        isOpen={isMatchedHotelDialogOpen}
        onAddHotelFromMatchedHotel={props.onAddHotelFromMatchedHotel}
        onClose={handleOnCloseMatchedHotelDialog}
        hotelInventory={hotelInventoryData}
        joyPlaceId={joyPlaceIdBySourceIdData?.joyPlaceBySourceId?.id}
      />
    </>
  );
};

const StyledHotelInputContainer = ({ children, isHotelInvalid, errorHotel }: { children: React.ReactNode; isHotelInvalid?: boolean; errorHotel?: string }) => (
  <Flex
    __css={{
      ['& > div']: {
        width: '100% !important',
      },
      [`${StyledInputContainer}`]: {
        border: isHotelInvalid || errorHotel ? '1px solid #c50020' : '1px solid transparent',
        borderRadius: '6px',
        _after: {
          border: '0px solid transparent',
          borderRadius: '6px',
          boxShadow: isHotelInvalid || errorHotel ? '0px 0 0px 1px #c50020' : '0px 0 0px 1px #333',
          content: '" "',
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
        },
        ['input']: {
          outline: 0,
          color: 'mono14',
          _hover: {
            borderColor: isHotelInvalid || errorHotel ? 'negative6' : 'mono14',
          },
          _focus: { outline: 0, borderColor: isHotelInvalid || errorHotel ? 'negative6' : 'mono14' },
          fontSize: '1rem !important',
        },
        ['textarea']: {
          outline: 0,
          _focus: { outline: 0 },
          fontSize: '1rem !important',
        },
      },
    }}
  >
    {children}
  </Flex>
);

const StyledTextAreaContainer = ({ children, isInvalid }: { children: React.ReactNode; isInvalid?: boolean }) => (
  <Flex
    __css={{
      ['& > div']: {
        width: '100% !important',
        height: '100%',
      },
      ['textarea']: {
        minHeight: pxToRem(112),
        maxHeight: pxToRem(400),
        width: '100%',
        minWidth: '100%',
        outline: 0,
        borderRadius: pxToRem(6),
        border: '0px solid',
        boxShadow: isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 1px #D9D9D9',
        _hover: { border: '0px solid', boxShadow: isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 1px #333' },
        _focus: { outline: 0, boxShadow: isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 2px #333' },
        color: 'mono14',
        fontSize: '1rem !important',
      },
    }}
  >
    {children}
  </Flex>
);
